import { FormGroup } from '@angular/forms';

export interface SimpleCameraProperty {
    property: string;
    value: number | number[];
}

export interface Property {
    property: string;
    dataType: string;
    value?: number;
    enumValue?: number;
    enumArray?: number[];
    isActive?: boolean;
}

export const enumKeysCameraSettings = {
    MODE_DIAL: 'modeDial',
    SHUTTER_SPEED: 'shutterSpeed',
    ISO_SPEED: 'isoSpeed',
    WHITE_BALANCE: 'whiteBalance',
    FIXED_MOVIE: 'fixedMovie',
    SHOOTING_MODE: 'shootingMode',
    METERING_MODE: 'meteringMode',
    DRIVE_MODE: 'driveMode',
    EXPOSURE: 'exposure',
    ASPECT_RATIO: 'aspectRatio',
    APERTURE_VALUE: 'apertureValue',
    FOCUS_MODE: 'focusMode',
    COLOR_TEMPERATURE: 'colorTemperature',
    AUTO_POWER_OFF_TIME: 'autoPowerOffTime',
    WHITE_BALANCE_SHIFT_BA: 'whiteBalanceShiftBA',
    STILL_PHOTO_IMAGE_QUALITY: 'stillPhotoImageQuality',
};

export class CameraPropertySet {
    properties: { [propertyName: string]: CameraProperty } = {};
    extras: { serialNumber: string; model: string } = { serialNumber: '', model: '' };
    isPhotographerRecentlyActive: boolean;
    lastUpdateDate?: number;
    enumKeys = enumKeysCameraSettings;

    constructor(cameraProperties: Property[]) {
        for (const property of cameraProperties) {
            this.properties[this.enumKeys[property.property]] = new CameraProperty(property);
        }
        for (const key of Object.values(this.enumKeys)) {
            if (!this.properties[key]) {
                this.properties[key] = <CameraProperty>{};
            }
        }
    }

    public getList(): CameraProperty[] {
        const cameraProperties = [];
        for (const property of Object.values(this.properties)) {
            if (!property.dataType || property.propertyValue === undefined || property.propertyValue === null) {
                continue;
            }
            cameraProperties.push(property);
        }

        return cameraProperties;
    }

    public update(form: FormGroup): void {
        for (const field in form.controls) {
            if (this.properties[field]) {
                this.properties[field].propertyValue = form.get(field).value;
            }
        }
    }

    public getMap(): { [key: string]: any } {
        const properties = {};
        for (const [name, property] of Object.entries(this.properties)) {
            properties[name] = property.propertyValue;
        }

        return properties;
    }

    getValue(property: string) {
        if (this.properties[property]) {
            return this.properties[property].propertyValue;
        }
    }

    setValue(property: string, value: any) {
        if (this.properties[property]) {
            this.properties[property].propertyValue = value;
        }
    }
}

export class CameraProperty {
    property: string;
    dataType: string;
    value: number;
    enumValue: number;
    enumArray: number[];

    constructor(property: Property) {
        this.property = property.property;
        this.dataType = property.dataType;
        this.value = property.value;
        this.enumValue = property.enumValue;
        this.enumArray = property.enumArray;
    }

    get dataTypeEnum() {
        return {
            Value: 'VALUE',
            EnumValue: 'ENUM_VALUE',
            EnumArray: 'ENUM_ARRAY',
        };
    }

    get propertyValue(): any {
        switch (this.dataType) {
            case this.dataTypeEnum.Value:
                return this.value;
            case this.dataTypeEnum.EnumValue:
                return this.enumValue;
            case this.dataTypeEnum.EnumArray:
                return this.enumArray ? this.enumArray.join('/') : '';
        }
    }

    set propertyValue(value: any) {
        switch (this.dataType) {
            case this.dataTypeEnum.Value:
                this.value = value;
                break;
            case this.dataTypeEnum.EnumValue:
                this.enumValue = value;
                break;
            case this.dataTypeEnum.EnumArray:
                this.enumArray = value.split('/').map((v) => +v);
                break;
        }
    }

    getProperty(): Property {
        return {
            dataType: this.dataType,
            enumArray: this.enumArray,
            enumValue: this.enumValue,
            property: this.property,
            value: this.value,
            isActive: false,
        };
    }
}
